import type { Role } from '@morph/spec';
import type { ResolvedParams } from './params.js';
import type { ObjectShape } from './types.js';

/**
 * The content-free portion of a template derived from a pool's conventions.
 *
 * Values deliberately do not cross this boundary. `unanimous` identifies enum
 * roles whose value may be resolved by the host from the store at create time.
 * Ordered scalars are intentionally excluded: equality is useful evidence for
 * enum defaults, but must not turn an incidental shared date or number into a
 * default for every newly created object.
 */
export interface DerivedTemplate {
  readonly roles: readonly Role[];
  readonly unanimous: readonly Role[];
}

export function deriveTemplate(
  shape: ObjectShape,
  params: ResolvedParams,
): DerivedTemplate {
  const roles = shape.roles
    .filter(({ coverage }) => coverage >= params.roleCoverageFloor)
    .map(({ role }) => role);
  // A sampled shape can only prove unanimity over its sample. Declining here
  // rather than at the call site is the whole point: `StoredShape extends
  // ObjectShape`, so every consumer of this function can already be handed one.
  const unanimous = shape.sampled ? [] : roles.filter((role) =>
    shape.roles.some((candidate) => candidate.role === role && candidate.coverage === 1)
      && shape.enumCardinality?.[role] === 1,
  );

  return { roles, unanimous };
}
